import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { LanguagePreference } from '../models/Translation';

const StorageKeys = {
  token: 'token',
  mobile: 'mobile',
  anonymous: 'anonymous',
  garIdo: 'bedici_gar_ido',
  language: 'bedici_language_preference',
  pendingPurchase: 'bedici_pending_purchase',
};

const TokenCookieKeys = ['access-token', 'access_token'];

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  private readCookie(name: string): string | null {
    try {
      const source = this.document?.cookie ?? '';
      if (!source) {
        return null;
      }

      const cookies = source.split(';');
      for (const entry of cookies) {
        const [rawKey, ...rawValue] = entry.split('=');
        if (!rawKey) {
          continue;
        }

        const key = rawKey.trim();
        if (key === name) {
          return decodeURIComponent(rawValue.join('='));
        }
      }
    } catch (error) {
      console.error('Failed to read cookie:', error);
    }

    return null;
  }

  private readTokenFromCookies(): string | null {
    for (const key of TokenCookieKeys) {
      const value = this.readCookie(key);
      if (value) {
        return value;
      }
    }

    return null;
  }

  private writeCookie(name: string, value: string, options?: { expires?: Date }): void {
    try {
      let cookie = `${name}=${encodeURIComponent(value)};path=/;SameSite=Lax`;
      const isHttps = this.document?.defaultView?.location?.protocol === 'https:';
      if (isHttps) {
        cookie += ';Secure';
      }

      if (options?.expires) {
        cookie += `;expires=${options.expires.toUTCString()}`;
      }

      this.document.cookie = cookie;
    } catch (error) {
      console.error('Failed to write cookie:', error);
    }
  }

  private removeCookie(name: string): void {
    this.writeCookie(name, '', { expires: new Date(0) });
  }

  getToken(): string | null {
    const storedToken = localStorage.getItem(StorageKeys.token);
    if (storedToken) {
      return storedToken;
    }

    const cookieToken = this.readTokenFromCookies();
    if (cookieToken) {
      try {
        localStorage.setItem(StorageKeys.token, cookieToken);
      } catch (error) {
        console.error('Failed to sync token from cookie to storage:', error);
      }
    }

    return cookieToken;
  }

  setToken(token: string) {
    localStorage.setItem(StorageKeys.token, token);
    for (const key of TokenCookieKeys) {
      this.writeCookie(key, token);
    }
  }

  deleteToken() {
    localStorage.removeItem(StorageKeys.token);
    for (const key of TokenCookieKeys) {
      this.removeCookie(key);
    }
  }

  setMobile(value: string) {
    localStorage.setItem(StorageKeys.mobile, value);
  }

  isMobile(): boolean {
    return localStorage.getItem(StorageKeys.mobile) !== null;
  }

  getMobile(): string | null {
    return localStorage.getItem(StorageKeys.mobile);
  }

  setAnonymous(value: boolean) {
    localStorage.setItem(StorageKeys.anonymous, `${value}`);
  }

  isAnonymous(): boolean {
    return localStorage.getItem(StorageKeys.anonymous) !== null;
  }

  deleteAnonymous() {
    return localStorage.removeItem(StorageKeys.anonymous);
  }

  setGarIdo(garIdo: string | null): void {
    if (garIdo) {
      localStorage.setItem(StorageKeys.garIdo, garIdo);
      return;
    }

    localStorage.removeItem(StorageKeys.garIdo);
  }

  getGarIdo(): string | null {
    return localStorage.getItem(StorageKeys.garIdo);
  }

  deleteGarIdo(): void {
    localStorage.removeItem(StorageKeys.garIdo);
  }

  clearAllLocalStorage(): void {
    this.deleteToken();
    localStorage.clear();
  }

  /**
   * Save language preference to localStorage
   */
  setLanguagePreference(languageCode: string): void {
    const preference: LanguagePreference = {
      code: languageCode,
      timestamp: Date.now(),
    };

    try {
      localStorage.setItem(StorageKeys.language, JSON.stringify(preference));
    } catch (error) {
      console.error('Failed to save language preference:', error);
    }
  }

  /**
   * Get language preference from localStorage
   */
  getLanguagePreference(): LanguagePreference | null {
    try {
      const stored = localStorage.getItem(StorageKeys.language);
      if (!stored) {
        return null;
      }

      const preference: LanguagePreference = JSON.parse(stored);

      // Validate the stored preference structure
      if (preference.code && typeof preference.code === 'string') {
        return preference;
      }
    } catch (error) {
      console.error('Failed to load language preference:', error);
    }

    return null;
  }

  /**
   * Remove language preference from localStorage
   */
  clearLanguagePreference(): void {
    try {
      localStorage.removeItem(StorageKeys.language);
    } catch (error) {
      console.error('Failed to clear language preference:', error);
    }
  }

  /**
   * Check if language preference exists
   */
  hasLanguagePreference(): boolean {
    return localStorage.getItem(StorageKeys.language) !== null;
  }

  /**
   * Save pending purchase data to localStorage
   */
  setPendingPurchase(selectedProduct: any, selectedOptions: any[]): void {
    const purchaseData = {
      selectedProduct,
      selectedOptions,
      timestamp: Date.now(),
    };

    try {
      localStorage.setItem(StorageKeys.pendingPurchase, JSON.stringify(purchaseData));
    } catch (error) {
      console.error('Failed to save pending purchase:', error);
    }
  }

  /**
   * Get pending purchase data from localStorage
   */
  getPendingPurchase(): { selectedProduct: any; selectedOptions: any[] } | null {
    try {
      const stored = localStorage.getItem(StorageKeys.pendingPurchase);
      if (!stored) {
        return null;
      }

      const purchaseData = JSON.parse(stored);

      // Validate the stored purchase data structure
      if (purchaseData.selectedProduct && Array.isArray(purchaseData.selectedOptions)) {
        return {
          selectedProduct: purchaseData.selectedProduct,
          selectedOptions: purchaseData.selectedOptions,
        };
      }
    } catch (error) {
      console.error('Failed to load pending purchase:', error);
    }

    return null;
  }

  /**
   * Remove pending purchase data from localStorage
   */
  clearPendingPurchase(): void {
    try {
      localStorage.removeItem(StorageKeys.pendingPurchase);
    } catch (error) {
      console.error('Failed to clear pending purchase:', error);
    }
  }

  /**
   * Check if pending purchase exists
   */
  hasPendingPurchase(): boolean {
    return localStorage.getItem(StorageKeys.pendingPurchase) !== null;
  }
}
